import { useEffect, useMemo, useRef, useState } from 'react';
import { Col, Row, message } from 'antd';
import { TableAction, BottomSheet, PageLayout } from '@/components';
import { useTranslation } from 'react-i18next';
import '@/styles/pickups.css';
import { useBulkCancelPickup } from '@/hooksApi/usePickup.ts';
import { useQueryClient } from '@tanstack/react-query';
import useConfirmModalStore from '@/stores/useConfirmModalStore.ts';
import { scrollTop } from '@/utils';
import { PickupPageTabEnum } from '@/types/pickup';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTE } from '@/config';
import usePickupTab from '@/hooks/usePickupTab';
import useRTOTab from '@/hooks/useRTOTab';
import { ProcessNameEnum, usePollingMechanismStore } from '@/stores/pollingMechanismStore';
import { useOpenEntityTicketModal } from '@/hooks';
import { TicketEntityIDFieldEnum } from '@/constants';
import { Search, X } from 'lucide-react';
import CurrentSlotBanner from '@/components/PickupSlots/CurrentSlotBanner';
import { useSelectAllPageSync } from './useSelectAllPageSync';
import RolloverToast from '@/components/PickupSlots/RolloverToast';
import DeferConfirmModal from '@/components/PickupSlots/DeferConfirmModal';
import { useRolloverToastSync, type RolloverCandidateRow } from './useRolloverToastSync';
import ChangeSlotModal from './ChangeSlotModal';

const SEARCH_DEBOUNCE_MS = 300;
const pageSize = 25;

const Pickups = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<PickupPageTabEnum>(() => {
    const tabParam = searchParams.get('tab');
    return tabParam === PickupPageTabEnum.RTO ? PickupPageTabEnum.RTO : PickupPageTabEnum.Pickups;
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);

  // Search state: raw input value and debounced value sent to the API
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '');
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') ?? '');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { openEntityTicketModal } = useOpenEntityTicketModal(TicketEntityIDFieldEnum.PICKUP);

  // Debounce the raw search input → update API query and reset to page 1
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
      if (searchInput !== searchQuery) {
        setCurrentPage(1);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Sync debounced search query to URL params
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (searchQuery.trim()) {
          next.set('search', searchQuery.trim());
        } else {
          next.delete('search');
        }
        return next;
      },
      { replace: true },
    );
  }, [searchQuery]);

  // Initialize tab from URL on mount and when URL changes
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const newTab =
      tabParam === PickupPageTabEnum.RTO ? PickupPageTabEnum.RTO : PickupPageTabEnum.Pickups;

    // Only update if the tab has changed
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [searchParams, activeTab]);

  const handleChangeTab = (newTab: PickupPageTabEnum) => {
    // Reset pagination and search when switching tabs
    setCurrentPage(1);
    setSearchInput('');
    setSearchQuery('');

    // Create new URLSearchParams — preserve only the tab parameter
    const newSearchParams = new URLSearchParams();

    // Only set the tab parameter if it's RTO
    if (newTab === PickupPageTabEnum.RTO) {
      newSearchParams.set('tab', newTab);
    }

    // Update URL with only the tab parameter (search cleared)
    setSearchParams(newSearchParams);

    // Update local state
    setActiveTab(newTab);
  };

  // Remove hash-based navigation effect as we're using query params now
  useEffect(() => {
    if (location.hash) {
      window.history.replaceState({}, '', location.pathname);
    }
  }, []);

  // Both hooks always enabled so tab badges show correct counts on load.
  // The active tab fetches with current page/search; the inactive tab
  // fetches page 1 without search just for the total count (lightweight).
  const isPickupActive = activeTab === PickupPageTabEnum.Pickups;
  const isRTOActive = activeTab === PickupPageTabEnum.RTO;

  const {
    data: pickupData,
    totals: pickupTotals,
    isFetching: pickupFetching,
    columns: pickupColumns,
    refetch: refetchPickupList,
  } = usePickupTab(
    isPickupActive ? currentPage : 1,
    isPickupActive ? pageSize : 1,
    true, // always enabled for count badge
    queryClient,
    isPickupActive ? (searchQuery || undefined) : undefined,
  );

  const {
    data: rtoData,
    totals: rtoTotals,
    isFetching: rtoFetching,
    columns: rtoColumns,
  } = useRTOTab(
    isRTOActive ? currentPage : 1,
    isRTOActive ? pageSize : 1,
    true, // always enabled for count badge
    isRTOActive ? (searchQuery || undefined) : undefined,
  );

  const { mutate: bulkCancelPickup, isLoading: isCancelingPickup } = useBulkCancelPickup();

  // Rollover toast: this is a polled SPA (no websocket/push) — derive the
  // toast target from the current Pickups-tab list itself (the most-recent
  // row with slot_response_open === true whose uid hasn't been
  // session-dismissed) and push it into useRolloverToastStore. Extracted into
  // useRolloverToastSync, which emits ONLY when the derived toast payload
  // actually changes — a content-equality bail that stops the pickup list's
  // row array from re-spinning React #185 ("Maximum update depth exceeded")
  // even if its reference churns. Only the Pickups tab has a slot concept.
  useRolloverToastSync(pickupData as unknown as RolloverCandidateRow[], isPickupActive);

  // Determine which data to use based on active tab
  const data = activeTab === PickupPageTabEnum.Pickups ? pickupData : rtoData;
  const totals = activeTab === PickupPageTabEnum.Pickups ? pickupTotals : rtoTotals;
  const isFetching = activeTab === PickupPageTabEnum.Pickups ? pickupFetching : rtoFetching;
  const columns = activeTab === PickupPageTabEnum.Pickups ? pickupColumns : rtoColumns;

  const tabItems = [
    {
      key: PickupPageTabEnum.Pickups,
      title: t('pickups'),
      children: null,
    },
    {
      key: PickupPageTabEnum.RTO,
      title: t('returns'),
      children: null,
    },
  ];

  const { openConfirm } = useConfirmModalStore();

  useEffect(() => {
    scrollTop();
  }, [currentPage]);

  useEffect(() => {
    // Reset selection when changing tabs
    setSelectedIds([]);
    setIsAllSelected(false);
    setCurrentPage(1);
  }, [activeTab]);

  const allOrdersInCurrentPage = useMemo(() => data.map((item: any) => item.key), [data]);

  // Keep the selection covering the visible page while "select all" is active,
  // as data changes — WITHOUT setState during render (which spun this page into
  // React error #185; see useSelectAllPageSync).
  useSelectAllPageSync(isAllSelected, allOrdersInCurrentPage, setSelectedIds);

  const handlePaginationChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleCancelPickup = () => {
    openConfirm({
      description: [t('cancel_pickup_confirm'), t('cancel_pickup_condition')],
      cancelText: t('cancel'),
      onConfirm: bulkCancel,
    });
  };

  const bulkCancel = () => {
    bulkCancelPickup(isAllSelected ? [] : selectedIds, {
      onSuccess: () => {
        // Invalidate appropriate cache based on active tab
        if (activeTab === PickupPageTabEnum.Pickups) {
          queryClient.invalidateQueries(['pickups']);
        } else {
          queryClient.invalidateQueries(['return_to_origin']);
        }
      },
      onError: () => { message.error(t('operation_failed_please_try_again')); },
    });
  };

  const handleChangeSelectedIds = (ids: string[]) => {
    if (isAllSelected) setIsAllSelected(false);
    setSelectedIds(ids);
  };

  const handleToggleIsAllSelected = () => {
    if (isAllSelected) setSelectedIds([]);
    setIsAllSelected(!isAllSelected);
  };

  const allOrdersInCurrentPageAreSelected =
    allOrdersInCurrentPage.length === selectedIds.length &&
    new Set(allOrdersInCurrentPage).size === new Set(selectedIds).size &&
    allOrdersInCurrentPage.every((item) => selectedIds.includes(item));

  return (
    <PageLayout
      bottomSheet={
        <BottomSheet
          sticky
          isVisible={selectedIds.length > 0}
          wrapperClassName='!border-none !shadow-[0_-2px_8px_rgba(0,0,0,0.06)] !rounded-xl !px-4 !py-3'
        >
          <div className='flex items-center justify-between w-full'>
            <div className='flex items-center gap-2'>
              <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='rgb(39,155,101)' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'><path d='M22 11.08V12a10 10 0 1 1-5.93-9.14'/><polyline points='22 4 12 14.01 9 11.01'/></svg>
              <span style={{ fontSize: 13, fontWeight: 500, color: 'rgb(57,57,60)' }}>
                {isAllSelected ? totals : selectedIds.length} {t('order_selected')}
              </span>
            </div>
            <div className='flex items-center gap-2'>
              <button
                type='button'
                onClick={() => {
                  setSelectedIds([]);
                  setIsAllSelected(false);
                }}
                style={{
                  height: 32, padding: '0 12px', fontSize: 12, fontWeight: 500,
                  color: 'rgb(115,123,140)', backgroundColor: 'transparent',
                  border: '1px solid rgb(232,230,227)', borderRadius: 8, cursor: 'pointer',
                }}
              >
                {t('deselect_all')}
              </button>
              <button
                type='button'
                onClick={handleCancelPickup}
                disabled={isCancelingPickup}
                style={{
                  height: 32, padding: '0 12px', fontSize: 12, fontWeight: 600,
                  color: '#fff', backgroundColor: 'rgb(220,40,40)',
                  border: 'none', borderRadius: 8, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                  opacity: isCancelingPickup ? 0.6 : 1,
                }}
              >
                <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><circle cx='12' cy='12' r='10'/><line x1='15' y1='9' x2='9' y2='15'/><line x1='9' y1='9' x2='15' y2='15'/></svg>
                {t('cancel')} {isAllSelected ? totals : selectedIds.length} {t('orders')}
              </button>
            </div>
          </div>
        </BottomSheet>
      }
    >
      <div className='flex flex-col gap-4 w-full'>
        {/* Slot preference banner — Pickups tab only, RTO has no slot concept */}
        {isPickupActive ? <CurrentSlotBanner /> : null}

        {/* Pill tabs */}
        <div className='flex items-center gap-2'>
          {tabItems.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type='button'
                onClick={() => handleChangeTab(tab.key as PickupPageTabEnum)}
                className={[
                  'h-9 px-4 rounded-full text-[13px] font-semibold whitespace-nowrap border border-solid transition-colors cursor-pointer active:scale-[0.98]',
                  isActive
                    ? 'bg-[rgb(237,224,74)] border-[rgb(237,224,74)] text-[rgb(57,57,60)]'
                    : 'bg-white border-[rgb(232,230,227)] text-[rgb(115,123,140)] hover:text-gray-700',
                ].join(' ')}
              >
                {tab.title}
                {tab.key === PickupPageTabEnum.Pickups && pickupTotals !== undefined && (
                  <span
                    className='ms-1.5 text-[11px] font-bold rounded-full px-1.5 py-0.5 bg-black/10 text-[rgb(57,57,60)]'
                  >
                    {pickupTotals}
                  </span>
                )}
                {tab.key === PickupPageTabEnum.RTO && rtoTotals !== undefined && (
                  <span
                    className='ms-1.5 text-[11px] font-bold rounded-full px-1.5 py-0.5 bg-black/10 text-[rgb(57,57,60)]'
                  >
                    {rtoTotals}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Info bar: showing range + total */}
        <div className='flex items-center justify-between px-1 -mt-1'>
          <span className='text-[12px] text-[rgb(115,123,140)]'>
            {totals !== undefined
              ? `${String(t('showing', { defaultValue: 'Showing' }))} ${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, totals)} ${String(t('of'))} ${totals}`
              : ''}
          </span>
          <span className='text-[12px] text-[rgb(115,123,140)]'>
            {totals
              ? `${String(t('page'))} ${currentPage} ${String(t('of'))} ${Math.ceil(totals / pageSize)}`
              : ''}
          </span>
        </div>

        {/* Table */}
        <Row className='w-full'>
          <Col md={24} xs={24}>
            <TableAction
              columns={columns}
              data={data}
              pageSize={pageSize}
              current={currentPage}
              total={totals}
              customClassName='table-pickup'
              onPaginationChange={handlePaginationChange}
              loading={isFetching}
              selectedRowKeys={selectedIds}
              setSelectedRowKeys={handleChangeSelectedIds}
              rowSelectionEnabled={activeTab === PickupPageTabEnum.Pickups}
              paginationFormat='showing_x_y_of_z'
              onRow={(record) => ({
                onClick: (e: React.MouseEvent) => {
                  // Don't navigate when clicking action buttons, dropdowns, or SVG icons
                  let target = e.target as HTMLElement;
                  while (target && target !== e.currentTarget) {
                    const tag = target.tagName?.toUpperCase();
                    if (tag === 'BUTTON' || tag === 'A' || tag === 'SVG' || tag === 'PATH' ||
                        target.getAttribute('role') === 'menuitem' ||
                        target.classList?.contains('ant-dropdown-trigger') ||
                        target.closest?.('.ant-dropdown')) {
                      return;
                    }
                    target = target.parentElement as HTMLElement;
                  }
                  if (activeTab === PickupPageTabEnum.RTO) {
                    navigate(`/pickups/rto/${record.key}`);
                  } else {
                    navigate(`${ROUTE.PICKUPS_DETAILS}/${record.key}`);
                  }
                },
                style: { cursor: 'pointer' },
              })}
            />
          </Col>
        </Row>
      </div>
      {/* Singleton change-slot modal — mounted once OUTSIDE the table row
          tree (per-row mounting froze the zoom-appear animation and left the
          modal non-interactive; see ChangeSlotModal). */}
      <ChangeSlotModal />
      <RolloverToast active={isPickupActive} />
      <DeferConfirmModal />
    </PageLayout>
  );
};

export default Pickups;
